import { useState } from "react";
import type { DeliveryAssetType } from "./asset-editor";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownEditor } from "@/components/ui/markdown-editor";

import { AssetEditor, type AssetDraft } from "./asset-editor";

export type DeliveryInitial = {
  livePreviewUrl: string;
  instructions: string;
  loomUrl: string;
  assets: {
    id: string;
    type: DeliveryAssetType;
    label: string;
    value: string;
    sensitive: boolean;
  }[];
};

export function DeliveryForm({
  orderId,
  initial,
}: {
  orderId: string;
  initial: DeliveryInitial;
}) {
  const [livePreviewUrl, setLivePreviewUrl] = useState(initial.livePreviewUrl);
  const [instructions, setInstructions] = useState(initial.instructions);
  const [loomUrl, setLoomUrl] = useState(initial.loomUrl);
  const [assets, setAssets] = useState<AssetDraft[]>(initial.assets);
  const [busy, setBusy] = useState(false);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();

    if (!livePreviewUrl.trim()) {
      toast.error("Live preview URL is required.");
      return;
    }
    if (!instructions.trim()) {
      toast.error("Handoff instructions are required.");
      return;
    }
    for (const a of assets) {
      if (!a.label.trim() || !a.value.trim()) {
        toast.error("Each asset needs a label and a value.");
        return;
      }
    }

    setBusy(true);
    try {
      const res = await fetch(`/api/admin/orders/${orderId}/delivery`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          livePreviewUrl: livePreviewUrl.trim(),
          instructions,
          loomUrl: loomUrl.trim() || undefined,
          assets: assets.map((a) => ({
            type: a.type,
            label: a.label.trim(),
            value: a.value.trim(),
            sensitive: a.sensitive,
          })),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        toast.error(body.error ?? "Could not save delivery");
        return;
      }
      toast.success("Delivery saved.");
      window.location.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <form onSubmit={onSave} className="space-y-5">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">Delivery package</h3>
          <p className="text-xs text-muted-foreground">
            Saving here is non-destructive — it doesn&apos;t notify the
            customer. Use &ldquo;Mark complete&rdquo; in the header when
            you&apos;re ready to ship.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="live-preview">Live preview URL</Label>
            <Input
              id="live-preview"
              type="url"
              value={livePreviewUrl}
              onChange={(e) => setLivePreviewUrl(e.target.value)}
              placeholder="https://…"
              required
              disabled={busy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="loom-url">
              Walkthrough video <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="loom-url"
              type="url"
              value={loomUrl}
              onChange={(e) => setLoomUrl(e.target.value)}
              placeholder="https://loom.com/share/…"
              disabled={busy}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Handoff instructions</Label>
          <MarkdownEditor
            value={instructions}
            onChange={setInstructions}
            placeholder="Markdown — how the customer uses what we shipped."
            ariaLabel="Handoff instructions"
            height={280}
            preview="edit"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Additional assets</Label>
          <p className="text-xs text-muted-foreground">
            Drag to reorder. The order shown here is the order the customer
            sees.
          </p>
          <AssetEditor assets={assets} onChange={setAssets} />
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save delivery"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
